import React, { useState } from "react";
import { ResponsiveModalSheet } from "@/shared/components/ResponsiveModalSheet";

import { Button, Heading, Label } from "@/shared/components/ui";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { PreventiveConfig, PreventiveOption } from "@/shared/utils/CoverageUtils";
import { Pet } from "@/shared/types/Quote.interface";
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";
import { PreventiveCards } from "./PreventiveCards";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { faClock, faDollarSign } from "@fortawesome/pro-solid-svg-icons";
import Strings from "../utils/Strings.constants";
import { useAppLayerContext } from "../contexts/AppLayer";
import { CheckedState } from "@radix-ui/react-checkbox";

interface PreventiveCareModalProps {
    isOpen: boolean;
    onClose: () => void;
    activePet: Pet;
    preventiveOptions?: PreventiveOption[];
    preventiveConfig: PreventiveConfig;
    underwriter: PetUnderwriterType;
    wellnessValue: "wellness-gold" | "wellness-platinum" | "None";
    onSave: (value: string, applyToAll: boolean) => Promise<void>;
    showMultiPetToggle?: boolean;
}

export const PreventiveCareModal: React.FC<PreventiveCareModalProps> = ({
    isOpen,
    onClose,
    activePet,
    preventiveOptions,
    preventiveConfig,
    underwriter,
    wellnessValue,
    onSave,
    showMultiPetToggle = false
}) => {
    const [selectedLevel, setSelectedLevel] = useState<string>(wellnessValue);
    const fieldId = "apply-to-all-pets";
    const [applyToAll, setApplyToAll] = useState(false);
    const {
        appState: { isQuoteUpdating }
    } = useAppLayerContext();

    const handleChange = (value: string) => {
        setSelectedLevel(value);
    };

    const handleSave = async () => {
        await onSave(selectedLevel, applyToAll);
        onClose();
    };

    const handleClose = () => {
        setSelectedLevel(wellnessValue);
        onClose();
    };

    return (
        <ResponsiveModalSheet
            isOpen={isOpen}
            onClose={handleClose}
            sheetSide="top"
            showCloseButton={false}
            styles={{
                modal: {
                    content:
                        "!inset-0 gap-0 !left-0 !top-0 bg-background-secondary !translate-x-0 !translate-y-0 !max-w-none !w-full !h-full !max-h-none !rounded-none !p-0 flex-col",
                    closeButton: "hidden"
                },
                sheet: {
                    content: "!h-[100dvh] overflow-y-auto p-0"
                }
            }}
        >
            <div className="flex justify-end sm:px-6">
                <Button
                    variant="ghost"
                    onClick={handleClose}
                    className="shrink-0 rounded-b-xl rounded-t-none bg-background-tertiary px-3 py-2 text-base font-medium leading-5 text-content-primary"
                >
                    <FontAwesomeIcon icon={faXmark} className="size-4" />
                    {Strings.CLOSE}
                </Button>
            </div>
            <div data-testid="preventive-care-modal" className=" mx-auto flex  max-w-[1152px] flex-col p-4 sm:p-6">
                {/* Header */}
                <div className="flex flex-col items-start gap-2 sm:justify-between md:flex-row">
                    <div>
                        <Heading level="h3" className="mb-2 flex text-base font-bold leading-5 lg:text-lg lg:leading-7">
                            <span className="shrink-0">Choose&nbsp;</span>
                            <span className="min-w-0 max-w-[100px] truncate sm:max-w-[200px] lg:max-w-[300px]"> {activePet.name}</span>
                            <span className="-ml-1 shrink-0">&apos;s preventive coverage</span>
                        </Heading>

                        <p className="text-sm font-medium leading-5 text-content-secondary lg:text-base">Get fixed reimbursement for wellness care, like dental cleanings.</p>
                    </div>
                    <div className="flex flex-row gap-2 md:flex-col md:self-end lg:flex-row">
                        <div className="flex items-center gap-1">
                            <FontAwesomeIcon icon={faClock} className="size-4 text-content-brand-b" />
                            <span className="text-xs font-bold leading-4 text-content-primary lg:text-base lg:leading-5">Next-day coverage</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <FontAwesomeIcon icon={faDollarSign} className="size-4 text-content-brand-b" />
                            <span className="text-xs font-bold leading-4 text-content-primary lg:text-base lg:leading-5">No deductible</span>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex flex-1 flex-col pt-4 md:pt-6">
                    <PreventiveCards
                        pet={activePet}
                        preventiveOptions={preventiveOptions ?? []}
                        preventiveConfig={preventiveConfig}
                        underwriter={underwriter}
                        wellnessLevel={selectedLevel}
                        handleChange={handleChange}
                        isCategorizedPreventiveEnabled={true}
                    />
                </div>

                {/* Sticky footer */}
                <div className="flex flex-col items-center gap-4 sm:mt-4 sm:flex-row">
                    <Button
                        loading={isQuoteUpdating}
                        variant="default-rounded"
                        onClick={handleSave}
                        className="mt-7 w-full px-4 py-3 text-base leading-5 text-content-primary-invert sm:order-last sm:mt-0 sm:w-[216px]"
                    >
                        {Strings.SAVE}
                    </Button>
                    <div className="flex w-full items-center justify-between ">
                        {showMultiPetToggle && (
                            <div className="order-1 flex items-center gap-2 sm:order-2">
                                <Checkbox
                                    id={fieldId}
                                    aria-labelledby={`${fieldId}-label`}
                                    checked={applyToAll}
                                    onCheckedChange={(value: CheckedState) => {
                                        if (value === "indeterminate") return;
                                        setApplyToAll(value);
                                    }}
                                    data-testid={fieldId}
                                />
                                <Label id={`${fieldId}-label`} htmlFor={fieldId} allowLabelClick className="text-sm leading-5 text-content-secondary">
                                    {Strings.MULTIPET_LINKED_LABEL}
                                </Label>
                            </div>
                        )}

                        <Button
                            variant="ghost"
                            onClick={handleClose}
                            className="order-2 ml-auto rounded-full bg-background-tertiary px-4 py-3 text-base font-medium leading-5 text-content-primary sm:order-1 sm:ml-0"
                        >
                            <FontAwesomeIcon icon={faXmark} className="size-4" />
                            {Strings.CLOSE}
                        </Button>
                    </div>
                </div>
            </div>
        </ResponsiveModalSheet>
    );
};
